import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  MessageSquare,
  Plus,
  Send,
  Paperclip,
  CheckCircle,
  Clock,
  AlertCircle,
  Search,
  Filter,
  MoreVertical,
  RefreshCw,
  User,
  Shield,
  FileText,
  Image as ImageIcon,
  Download,
  X,
  ChevronLeft,
  Bell,
  Settings,
  Inbox,
  CheckCheck,
  AlertTriangle,
  Loader2,
  Upload,
  Trash2
} from "lucide-react";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Notification sound
const playNotificationSound = () => {
  try {
    const audio = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp+cnp2RfGZcX2x+kJydnJmShXFhW2N0hpWdnZqWjH1uZGZxf42Ynp2amI+CdG1rbXeBjpifnZmVjIB1cXBzfIiUm56cmZOKgHl1dnh+iJOanJuYk4qAfHl5fICHkJibnJmUjoWAfn1+gYaOlpudmpaQiIOBgIGEiI+Wm5yZlpCIhIOCg4aKkJaanJmWkYqFhIOEh4uQlpqcmZaRi4aEhIWIjJGWmpuZlpGLh4WFhomMkZaampqWkYyIhoaGiYyRlZmampWRjImHh4iKjZGVmZqZlZGMioiIiYuOkpWYmpmVkY2KiYmKjI+SlZiZmJWRjYuKiouNj5KVmJiYlZGNi4qKi42PkpWXmJeVkY2Li4uMjpCSlZeXl5SRjYyLi4yOkJKVl5eWlJGNjIyMjY+RlJaXlpWRjo2MjI2PkZOVl5aVkY+NjI2Nj5GTlZaWlJGPjY2NjY+RlJWWlZSRj42NjY6QkpSVlpSRkI6OjY6PkZOUlZSTkI+OjY6PkJKUlJSTkY+Ojo6PkJKTlJSTkY+Ojo6PkJKTlJOSkI+Pjo6PkJKTlJOSkJCPj4+QkZKTk5KRkJCPj4+QkZKTk5KRkJCPj4+QkZKTk5KRkJCQkJCQkZKTkpKRkJCQkJCQkZKSkpKRkJCQkJCQkZKSkpKRkJCQkJCQkZKSkpKRkZGQkJCQkZKSkpKRkZGQkJCRkZKSkpKRkZGQkJGRkZKSkpKRkZGRkZGRkZKSkpKRkZGRkZGRkZKSkpKRkZGRkZGRkZKSkpKRkZGRkZGRkZGRkpKSkZGRkZGRkZGRkZGRkZGRkZGR");
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch (e) {
    // Ignore audio errors
  }
};

const SupportPage = () => {
  usePageTitle("Support");
  const { token, user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ open: 0, waiting_reply: 0, resolved: 0, urgent: 0, unread_messages: 0 });
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Selected ticket and messages
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  
  // New ticket dialog
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicketForm, setNewTicketForm] = useState({
    subject: "",
    category_id: "",
    priority: "normal",
    message: ""
  });
  const [creatingTicket, setCreatingTicket] = useState(false);
  
  // Message input
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Admin create ticket dialog
  const [showAdminCreate, setShowAdminCreate] = useState(false);
  const [users, setUsers] = useState([]);
  const [adminTicketForm, setAdminTicketForm] = useState({
    target_user_id: "",
    subject: "",
    category_id: "",
    priority: "normal",
    message: ""
  });
  
  // Category management (admin)
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [adminCategories, setAdminCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
    roles: [],
    color: "#6366f1",
    is_active: true
  });
  
  // Refs
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastUnreadCount = useRef(0);
  
  const isAdmin = user?.role === "super_admin";

  // Fetch data
  const fetchTickets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      
      const response = await fetch(`${BACKEND_URL}/api/support/tickets?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setTickets(data.tickets || []);
    } catch (error) {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error("Failed to load categories");
    }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/tickets/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      // Check for new unread messages and play sound
      if (data.unread_messages > lastUnreadCount.current && lastUnreadCount.current > 0) {
        playNotificationSound();
      }
      lastUnreadCount.current = data.unread_messages;
      
      setStats(data);
    } catch (error) {
      console.error("Failed to load stats");
    }
  }, [token]);

  const fetchMessages = useCallback(async (ticketId) => {
    setLoadingMessages(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setSelectedTicket(data.ticket);
      setMessages(data.messages || []);
      
      // Refresh stats to update unread counts
      fetchStats();
    } catch (error) {
      toast.error("Failed to load messages");
    } finally {
      setLoadingMessages(false);
    }
  }, [token, fetchStats]);

  // Initial load
  useEffect(() => {
    fetchTickets();
    fetchCategories();
    fetchStats();
  }, [fetchTickets, fetchCategories, fetchStats]);

  // Polling for real-time updates (every 10 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchStats();
      if (selectedTicket) {
        fetchMessages(selectedTicket.id);
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [selectedTicket, fetchStats, fetchMessages]);

  // Scroll to bottom when new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Create new ticket
  const handleCreateTicket = async () => {
    if (!newTicketForm.subject.trim() || !newTicketForm.category_id) {
      toast.error("Please fill in subject and category");
      return;
    }
    
    setCreatingTicket(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/tickets`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newTicketForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success("Ticket created successfully");
        setShowNewTicket(false);
        setNewTicketForm({ subject: "", category_id: "", priority: "normal", message: "" });
        fetchTickets();
        fetchStats();
        // Open the new ticket
        fetchMessages(data.id);
      } else {
        toast.error("Failed to create ticket");
      }
    } catch (error) {
      toast.error("Failed to create ticket");
    } finally {
      setCreatingTicket(false);
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;
    if (!selectedTicket) return;
    
    setSendingMessage(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/tickets/${selectedTicket.id}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          content: newMessage,
          attachments: attachments.map(a => a.url)
        })
      });
      
      if (response.ok) {
        setNewMessage("");
        setAttachments([]);
        fetchMessages(selectedTicket.id);
        fetchTickets();
      } else {
        toast.error("Failed to send message");
      }
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  // File upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File size exceeds 50MB limit");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await fetch(`${BACKEND_URL}/api/support/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setAttachments(prev => [...prev, {
          filename: data.filename,
          url: data.url,
          type: data.type,
          size: data.size
        }]);
        toast.success("File uploaded");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Upload failed");
      }
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Resolve ticket
  const handleResolve = async () => {
    if (!selectedTicket) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/tickets/${selectedTicket.id}/resolve`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success("Ticket resolved");
        fetchMessages(selectedTicket.id);
        fetchTickets();
        fetchStats();
      }
    } catch (error) {
      toast.error("Failed to resolve ticket");
    }
  };

  // Reopen ticket
  const handleReopen = async () => {
    if (!selectedTicket) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/tickets/${selectedTicket.id}/reopen`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success("Ticket reopened");
        fetchMessages(selectedTicket.id);
        fetchTickets();
        fetchStats();
      }
    } catch (error) {
      toast.error("Failed to reopen ticket");
    }
  };

  // Toggle priority
  const handleTogglePriority = async () => {
    if (!selectedTicket) return;
    const newPriority = selectedTicket.priority === "urgent" ? "normal" : "urgent";
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/tickets/${selectedTicket.id}/priority?priority=${newPriority}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(`Priority set to ${newPriority}`);
        fetchMessages(selectedTicket.id);
        fetchTickets();
      }
    } catch (error) {
      toast.error("Failed to update priority");
    }
  };

  // Delete resolved ticket permanently (admin only)
  const handleDeleteTicket = async () => {
    if (!selectedTicket || selectedTicket.status !== "resolved") {
      toast.error("Only resolved tickets can be deleted");
      return;
    }
    
    if (!window.confirm(`Are you sure you want to permanently delete this ticket?\n\nSubject: "${selectedTicket.subject}"\n\nThis action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/tickets/${selectedTicket.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success("Ticket deleted permanently");
        setSelectedTicket(null);
        setMessages([]);
        fetchTickets();
        fetchStats();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to delete ticket");
      }
    } catch (error) {
      toast.error("Failed to delete ticket");
    }
  };

  // Admin: Create ticket to user
  const handleAdminCreateTicket = async () => {
    if (!adminTicketForm.target_user_id || !adminTicketForm.subject.trim()) {
      toast.error("Please select user and enter subject");
      return;
    }
    
    try {
      const params = new URLSearchParams({
        target_user_id: adminTicketForm.target_user_id,
        subject: adminTicketForm.subject,
        message: adminTicketForm.message,
        category_id: adminTicketForm.category_id || categories[0]?.id || "",
        priority: adminTicketForm.priority
      });
      
      const response = await fetch(`${BACKEND_URL}/api/support/tickets/admin-create?${params}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success("Ticket created for user");
        setShowAdminCreate(false);
        setAdminTicketForm({ target_user_id: "", subject: "", category_id: "", priority: "normal", message: "" });
        fetchTickets();
        fetchStats();
      }
    } catch (error) {
      toast.error("Failed to create ticket");
    }
  };

  // Fetch users for admin
  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error("Failed to load users");
    }
  };

  // Seed default categories
  const handleSeedCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/categories/seed-default`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      toast.success(data.message);
      fetchCategories();
    } catch (error) {
      toast.error("Failed to seed categories");
    }
  };

  // Filter tickets
  const filteredTickets = tickets.filter(ticket => {
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      return ticket.subject.toLowerCase().includes(search) ||
             ticket.created_by_username?.toLowerCase().includes(search);
    }
    return true;
  });

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case "open":
        return <Badge className="bg-blue-100 text-blue-700 border-blue-300">Open</Badge>;
      case "waiting_reply":
        return <Badge className="bg-amber-100 text-amber-700 border-amber-300">Waiting Reply</Badge>;
      case "resolved":
        return <Badge className="bg-green-100 text-green-700 border-green-300">Resolved</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Get priority badge
  const getPriorityBadge = (priority) => {
    if (priority === "urgent") {
      return <Badge className="bg-red-100 text-red-700 border-red-300">Urgent</Badge>;
    }
    return null;
  };

  // Format time
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  // Render attachment
  const renderAttachment = (url, filename) => {
    const ext = url.split('.').pop().toLowerCase();
    const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
    
    if (isImage) {
      return (
        <a href={`${BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer" className="block">
          <img 
            src={`${BACKEND_URL}${url}`} 
            alt={filename} 
            className="max-w-xs max-h-48 rounded-lg border shadow-sm hover:shadow-md transition-shadow"
          />
        </a>
      );
    }
    
    return (
      <a 
        href={`${BACKEND_URL}${url}`} 
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
      >
        <FileText className="w-4 h-4 text-slate-600" />
        <span className="text-sm text-slate-700">{filename || "Download file"}</span>
        <Download className="w-4 h-4 text-slate-500" />
      </a>
    );
  };

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col animate-fade-in" data-testid="support-page">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground">Support Center</h1>
            <p className="text-xs text-muted-foreground">
              {isAdmin ? "Manage all support tickets" : "Get help from admin"}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Stats badges */}
          <div className="hidden md:flex items-center gap-2 mr-4">
            <Badge variant="outline" className="gap-1">
              <Inbox className="w-3 h-3" />
              {stats.open} Open
            </Badge>
            <Badge variant="outline" className="gap-1 text-amber-600 border-amber-300">
              <Clock className="w-3 h-3" />
              {stats.waiting_reply} Waiting
            </Badge>
            {stats.urgent > 0 && (
              <Badge className="gap-1 bg-red-100 text-red-700 border-red-300">
                <AlertTriangle className="w-3 h-3" />
                {stats.urgent} Urgent
              </Badge>
            )}
            {stats.unread_messages > 0 && (
              <Badge className="gap-1 bg-blue-100 text-blue-700 border-blue-300">
                <Bell className="w-3 h-3" />
                {stats.unread_messages} Unread
              </Badge>
            )}
          </div>
          
          <Button variant="outline" size="sm" onClick={() => { fetchTickets(); fetchStats(); }}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          
          {isAdmin && (
            <>
              <Button variant="outline" size="sm" onClick={() => { setShowAdminCreate(true); fetchUsers(); }}>
                <User className="w-4 h-4 mr-1" />
                Message User
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setShowCategoryManager(true); handleSeedCategories(); }}>
                <Settings className="w-4 h-4" />
              </Button>
            </>
          )}
          
          <Button size="sm" onClick={() => setShowNewTicket(true)} className="gap-1 bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4" />
            New Ticket
          </Button>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div className="flex-1 flex overflow-hidden">
        {/* Ticket List */}
        <div className={cn(
          "w-full md:w-96 border-r bg-slate-50 flex flex-col",
          selectedTicket && "hidden md:flex"
        )}>
          {/* Filters */}
          <div className="p-3 border-b bg-white space-y-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search tickets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8"
              />
            </div>
            <div className="flex gap-1">
              {["all", "open", "waiting_reply", "resolved"].map((status) => (
                <Button
                  key={status}
                  variant={statusFilter === status ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setStatusFilter(status)}
                  className="h-7 text-xs capitalize"
                >
                  {status === "waiting_reply" ? "Waiting" : status}
                </Button>
              ))}
            </div>
          </div>
          
          {/* Ticket List */}
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No tickets found</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    onClick={() => fetchMessages(ticket.id)}
                    className={cn(
                      "p-3 cursor-pointer hover:bg-white transition-colors",
                      selectedTicket?.id === ticket.id && "bg-white border-l-2 border-blue-500",
                      ticket.unread_count > 0 && "bg-blue-50"
                    )}
                    data-testid={`ticket-${ticket.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {ticket.priority === "urgent" && (
                            <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                          )}
                          <span className="font-medium text-sm truncate">{ticket.subject}</span>
                          {ticket.unread_count > 0 && (
                            <Badge className="h-4 px-1.5 text-xs bg-blue-500">{ticket.unread_count}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          {isAdmin && (
                            <span className="font-medium text-slate-700">
                              {ticket.initiated_by_admin ? `To: ${ticket.assigned_to_username}` : ticket.created_by_username}
                            </span>
                          )}
                          <span>•</span>
                          <span>{ticket.category_name}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-xs text-slate-400">{formatTime(ticket.updated_at)}</span>
                        {getStatusBadge(ticket.status)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className={cn(
          "flex-1 flex flex-col bg-white",
          !selectedTicket && "hidden md:flex"
        )}>
          {!selectedTicket ? (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-lg font-medium">Select a ticket to view</p>
                <p className="text-sm">Or create a new support ticket</p>
              </div>
            </div>
          ) : (
            <>
              {/* Ticket Header */}
              <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="md:hidden"
                    onClick={() => setSelectedTicket(null)}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="font-semibold">{selectedTicket.subject}</h2>
                      {getPriorityBadge(selectedTicket.priority)}
                      {getStatusBadge(selectedTicket.status)}
                    </div>
                    <div className="text-xs text-slate-500">
                      {selectedTicket.category_name} • {selectedTicket.message_count} messages
                      {isAdmin && ` • From: ${selectedTicket.created_by_username}`}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={handleTogglePriority}>
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          {selectedTicket.priority === "urgent" ? "Set Normal" : "Set Urgent"}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {selectedTicket.status === "resolved" ? (
                          <>
                            <DropdownMenuItem onClick={handleReopen}>
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Reopen Ticket
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={handleDeleteTicket} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Permanently
                            </DropdownMenuItem>
                          </>
                        ) : (
                          <DropdownMenuItem onClick={handleResolve} className="text-green-600">
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Mark Resolved
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                {loadingMessages ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg) => {
                      const isOwnMessage = msg.sender_id === user?.id;
                      const isAdminMessage = msg.sender_role === "super_admin";
                      
                      return (
                        <div
                          key={msg.id}
                          className={cn(
                            "flex",
                            isOwnMessage ? "justify-end" : "justify-start"
                          )}
                        >
                          <div className={cn(
                            "max-w-[75%] rounded-2xl px-4 py-2.5",
                            isOwnMessage 
                              ? "bg-blue-500 text-white rounded-br-md" 
                              : isAdminMessage 
                                ? "bg-indigo-100 text-indigo-900 rounded-bl-md border border-indigo-200"
                                : "bg-slate-100 text-slate-800 rounded-bl-md"
                          )}>
                            {!isOwnMessage && (
                              <div className="flex items-center gap-1.5 mb-1">
                                {isAdminMessage && <Shield className="w-3 h-3" />}
                                <span className="text-xs font-medium">{msg.sender_username}</span>
                              </div>
                            )}
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            {msg.attachments?.length > 0 && (
                              <div className="mt-2 space-y-2">
                                {msg.attachments.map((url, idx) => (
                                  <div key={idx}>
                                    {renderAttachment(url)}
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className={cn(
                              "text-xs mt-1",
                              isOwnMessage ? "text-blue-100" : "text-slate-400"
                            )}>
                              {formatTime(msg.created_at)}
                              {isOwnMessage && msg.read_by?.length > 1 && (
                                <CheckCheck className="w-3 h-3 inline ml-1" />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Message Input */}
              {selectedTicket.status !== "resolved" && (
                <div className="p-3 border-t bg-slate-50">
                  {/* Attachments preview */}
                  {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {attachments.map((att, idx) => (
                        <div key={idx} className="flex items-center gap-1 px-2 py-1 bg-white rounded border text-xs">
                          <FileText className="w-3 h-3" />
                          <span className="max-w-[100px] truncate">{att.filename}</span>
                          <button onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}>
                            <X className="w-3 h-3 text-slate-400 hover:text-red-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-end gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      accept=".xlsx,.xls,.pdf,.png,.jpg,.jpeg,.gif,.webp"
                      onChange={handleFileUpload}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="h-9"
                    >
                      {uploading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Paperclip className="w-4 h-4" />
                      )}
                    </Button>
                    
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 min-h-[40px] max-h-32 resize-none"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    />
                    
                    <Button
                      onClick={handleSendMessage}
                      disabled={sendingMessage || (!newMessage.trim() && attachments.length === 0)}
                      className="h-9 bg-blue-600 hover:bg-blue-700"
                    >
                      {sendingMessage ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Resolved banner */}
              {selectedTicket.status === "resolved" && (
                <div className="p-3 border-t bg-green-50 text-center">
                  <div className="flex items-center justify-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">This ticket has been resolved</span>
                  </div>
                  {(isAdmin || selectedTicket.created_by === user?.id) && (
                    <Button variant="link" size="sm" onClick={handleReopen} className="mt-1">
                      Reopen ticket
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* New Ticket Dialog */}
      <Dialog open={showNewTicket} onOpenChange={setShowNewTicket}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-500" />
              Create New Ticket
            </DialogTitle>
            <DialogDescription>
              Describe your issue and we will help you resolve it.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={newTicketForm.category_id}
                onValueChange={(v) => setNewTicketForm(prev => ({ ...prev, category_id: v }))}
              >
                <SelectTrigger data-testid="ticket-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={newTicketForm.subject}
                onChange={(e) => setNewTicketForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Brief description of your issue"
                data-testid="ticket-subject-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={newTicketForm.priority}
                onValueChange={(v) => setNewTicketForm(prev => ({ ...prev, priority: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Message (Optional)</Label>
              <Textarea
                value={newTicketForm.message}
                onChange={(e) => setNewTicketForm(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Describe your issue in detail..."
                className="min-h-[100px]"
                data-testid="ticket-message-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewTicket(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTicket} disabled={creatingTicket} data-testid="create-ticket-btn">
              {creatingTicket ? "Creating..." : "Create Ticket"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin: Create Ticket to User Dialog */}
      <Dialog open={showAdminCreate} onOpenChange={setShowAdminCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-500" />
              Message a User
            </DialogTitle>
            <DialogDescription>
              Start a conversation with a specific user.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select User</Label>
              <Select
                value={adminTicketForm.target_user_id}
                onValueChange={(v) => setAdminTicketForm(prev => ({ ...prev, target_user_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.display_name || u.username} ({u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={adminTicketForm.subject}
                onChange={(e) => setAdminTicketForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Message subject"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                value={adminTicketForm.message}
                onChange={(e) => setAdminTicketForm(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Your message..."
                className="min-h-[100px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdminCreate(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdminCreateTicket}>
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportPage;
